// Fallback de FAVORITOS em localStorage.
//
// POR QUE EXISTE: os favoritos moram no IndexedDB (local-db, store `favorites`).
// Em TVs webOS/Tizen 2017+ a quota de IDB é apertada e o SO trata "temporary
// storage" como EVICTABLE — ao longo de semanas ela esgota e os `put` passam a
// REJEITAR. O progresso já tinha rede de segurança (watch-progress-fallback) e
// os canais recentes também (channel-views-fallback); favorito era a única das
// três superfícies pessoais SEM nenhuma: `addFavorite`/`removeFavorite`/
// `isFavorite`/`getFavorites` não tinham um único `try/catch`, então a rejeição
// subia como promise não tratada e a estrela simplesmente não virava.
//
// ── POR QUE ESTE MÓDULO NÃO É UMA CÓPIA DO watch-progress-fallback ──
// Progresso é um valor MONOTÔNICO que só avança: espelhar o último `put` basta,
// e o LRU pode descartar o mais velho sem mentir. Favorito é um BOOLEANO COM
// REMOÇÃO, e é aí que mora o erro fácil:
//
//   o usuário DESFAVORITA com o IDB morto. O `delete` não chega ao disco, então
//   o registro CONTINUA no IDB. Se o overlay só soubesse falar "existe", a
//   próxima leitura leria o IDB, encontraria o registro e RESSUSCITARIA o
//   favorito que o usuário acabou de tirar.
//
// Por isso o overlay guarda LÁPIDES (`deleted: true`): uma entrada que afirma
// "este id foi REMOVIDO", e que SUPRIME o que o IDB devolver. Sem lápide o
// fallback conserta a escrita e quebra a remoção.
//
// ── PRECEDÊNCIA: O OVERLAY SEMPRE VENCE ──
// Não é merge por timestamp, é sobreposição. Uma entrada só nasce aqui quando o
// IDB RECUSOU a operação — ou seja, para aquele id o overlay é estritamente mais
// novo que o disco. E ela morre assim que o IDB ACEITA a mesma operação
// (`fallbackForgetFavorite`), então o overlay nunca fica "por cima" de um IDB
// que já sabe a verdade. Mesma disciplina do putPosition: sucesso no IDB →
// descarta a cópia de fallback.
//
// ── O QUE GARANTE A RECONCILIAÇÃO: SUBSTITUIÇÃO POR ID, NÃO O RELÓGIO ──
// ⚠️ Leia isto antes de "melhorar" a reconciliação.
//
// Um id tem NO MÁXIMO UMA entrada, e `upsertEntry` a SUBSTITUI
// incondicionalmente (`list[idx] = entry`). A última operação vence porque foi
// a última a ESCREVER, não porque ganhou uma comparação. NÃO HÁ DESEMPATE aqui
// — e é justamente por isso que o esquema é correto: favoritar e desfavoritar o
// mesmo item no mesmo milissegundo, em qualquer ordem, resolve certo sem
// consultar relógio nenhum.
//
// O PERIGO é alguém introduzir uma comparação por timestamp ("só sobrescreve se
// for mais novo"). Aí sim dois toques no mesmo tique passariam a empatar, e o
// bug que hoje não existe nasceria do conserto. Se algum dia for preciso ordem
// causal, use um contador monotônico — nunca o relógio de parede, que anda para
// trás (NTP, fuso, usuário mexendo na data da TV).
//
// ── PARA QUE O `updatedAt` SERVE, ENTÃO ──
// Só para ordenar o CORTE DO TETO em `writeAll` (LRU): passando de
// FAVORITES_FALLBACK_MAX_ENTRIES, sobrevivem as entradas de maior `updatedAt`.
// É uso de ORDEM, não de desempate — e é só por causa dele que o relógio é em
// ms: em segundos um lote inteiro empata, o `sort` estável preserva a ordem de
// inserção e o corte descarta justamente as MAIS RECENTES.
// `Favorite.addedAt` (campo de PRODUTO, em segundos) é outro relógio, e ordena
// a lista exibida — não confundir os dois.
//
// INVARIANTE: NUNCA lança. Qualquer erro de storage (indisponível, cheio, modo
// privado, file:// restrito) é engolido — a tela jamais quebra por causa do
// fallback.
//
// COMPAT Chr38 (piso do app de TV): só JSON, Array e Date.now. Sem `Proxy`, sem
// `Object.fromEntries`, sem spread de Map. `Map`/`Set` são nativos desde o Chr38
// e já são usados no local-db.

import type { Favorite } from './local-db'

const STORAGE_KEY = 'swipe:fav-fallback:v1'

/**
 * Teto de entradas no overlay. Só acumula durante uma JANELA DE FALHA do IDB
 * (em device saudável o bucket fica vazio), e o produto trabalha com <100
 * favoritos por scope — 200 dá o dobro de folga sobre o pior caso realista.
 *
 * ⚠️ Estouro é lesivo nos DOIS sentidos (descartar um add perde um favorito;
 * descartar uma lápide RESSUSCITA um favorito removido), por isso o teto é
 * generoso em vez de apertado: aqui o barato sai caro.
 */
export const FAVORITES_FALLBACK_MAX_ENTRIES = 200

/**
 * Uma operação de favorito que o IDB recusou.
 *
 * `deleted: false` → "este favorito EXISTE" (o add não chegou ao disco).
 * `deleted: true`  → LÁPIDE: "este favorito foi REMOVIDO" (o delete não chegou
 *                    ao disco, então o registro pode continuar lá).
 */
export interface FavoriteFallbackEntry {
  /** id scopado (`scope::type:itemId`). Presente inclusive na lápide. */
  id: string
  /** Scope dono (`p:CODE::pf:ID`). A lápide também precisa, pra ser varrida
   *  pelos "limpar dados" por scope/provider. */
  providerScope: string
  /** Lápide? Ver o cabeçalho: é o que impede a ressurreição. */
  deleted: boolean
  /** Relógio de reconciliação em MILISSEGUNDOS. Ver o cabeçalho. */
  updatedAt: number
  /** O registro completo. Ausente na lápide (não há o que repintar). */
  favorite?: Favorite
}

/** Acesso defensivo ao localStorage — pode nem existir (SSR/teste node) ou
 *  lançar ao ser tocado (alguns WebViews em file://). */
function store(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

/** Type guard do registro de produto (pode vir corrompido/parcial do JSON). */
function isFavoriteRecord(v: unknown): v is Favorite {
  if (!v || typeof v !== 'object') return false
  const f = v as Record<string, unknown>
  return (
    typeof f['id'] === 'string' &&
    typeof f['providerScope'] === 'string' &&
    typeof f['type'] === 'string' &&
    typeof f['itemId'] === 'string' &&
    typeof f['addedAt'] === 'number'
  )
}

/** Type guard da entrada do overlay. Uma entrada NÃO-lápide sem `favorite`
 *  válido é lixo (não dá pra repintar o card): cai fora na leitura. */
function isEntry(v: unknown): v is FavoriteFallbackEntry {
  if (!v || typeof v !== 'object') return false
  const e = v as Record<string, unknown>
  if (
    typeof e['id'] !== 'string' ||
    typeof e['providerScope'] !== 'string' ||
    typeof e['deleted'] !== 'boolean' ||
    typeof e['updatedAt'] !== 'number'
  ) {
    return false
  }
  return e['deleted'] === true || isFavoriteRecord(e['favorite'])
}

function readAll(): FavoriteFallbackEntry[] {
  const s = store()
  if (!s) return []
  try {
    const raw = s.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isEntry)
  } catch {
    return []
  }
}

/**
 * @returns `true` se o bucket foi de fato gravado.
 *
 * O retorno existe pra a UI poder ser HONESTA: quando o IDB recusa E o
 * localStorage também (modo privado, cota do próprio localStorage, file://
 * restrito), a operação não persistiu em lugar nenhum e a estrela otimista
 * precisa VOLTAR. Sem este sinal, "nunca lança" viraria "sempre finge que deu
 * certo" — e a mentira na tela é pior que o erro.
 */
function writeAll(list: FavoriteFallbackEntry[]): boolean {
  const s = store()
  if (!s) return false
  try {
    const bounded = [...list]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, FAVORITES_FALLBACK_MAX_ENTRIES)
    s.setItem(STORAGE_KEY, JSON.stringify(bounded))
    return true
  } catch {
    // localStorage cheio/indisponível — engole (nunca quebra a tela), mas
    // REPORTA, pra quem pintou otimista poder reverter.
    return false
  }
}

/** Substitui (ou insere) a entrada daquele id. Um id tem no máximo UMA
 *  entrada: a última operação é a que vale. */
function upsertEntry(entry: FavoriteFallbackEntry): boolean {
  const list = readAll()
  const idx = list.findIndex((e) => e.id === entry.id)
  if (idx >= 0) list[idx] = entry
  else list.push(entry)
  return writeAll(list)
}

/** O IDB RECUSOU um add: o overlay passa a afirmar que este favorito existe.
 *  @returns `true` se conseguiu registrar. */
export function fallbackUpsertFavorite(record: Favorite): boolean {
  return upsertEntry({
    id: record.id,
    providerScope: record.providerScope,
    deleted: false,
    updatedAt: Date.now(),
    favorite: record,
  })
}

/** O IDB RECUSOU um delete: grava a LÁPIDE. Sem isto o registro continua no
 *  disco e a próxima leitura o ressuscita.
 *  @returns `true` se conseguiu registrar a lápide. */
export function fallbackTombstoneFavorite(id: string, providerScope: string): boolean {
  return upsertEntry({ id, providerScope, deleted: true, updatedAt: Date.now() })
}

/** O IDB ACEITOU a operação: o disco virou a verdade e o overlay não tem mais
 *  o que dizer sobre este id. Mantém o bucket enxuto e impede que uma entrada
 *  velha sobreponha um IDB já correto. */
export function fallbackForgetFavorite(id: string): void {
  const list = readAll()
  const next = list.filter((e) => e.id !== id)
  if (next.length !== list.length) writeAll(next)
}

/** Entradas de um scope (provider+profile) exato. */
export function fallbackReadFavoriteEntries(scope: string): FavoriteFallbackEntry[] {
  return readAll().filter((e) => e.providerScope === scope)
}

/**
 * Veredito do overlay para um id:
 *   `true`      → o overlay afirma que É favorito
 *   `false`     → o overlay afirma que NÃO é (lápide)
 *   `undefined` → o overlay NÃO TEM OPINIÃO; quem responde é o IDB
 *
 * Os três casos são distintos de propósito: colapsar `undefined` em `false`
 * faria toda tela sem entrada de overlay (ou seja, o device saudável) reportar
 * "não é favorito" sem nunca perguntar ao disco.
 */
export function fallbackFavoriteVerdict(id: string): boolean | undefined {
  const hit = readAll().find((e) => e.id === id)
  if (!hit) return undefined
  return !hit.deleted
}

/**
 * NÚCLEO PURO da reconciliação (testável sem IDB e sem localStorage).
 *
 * Sobrepõe as entradas do overlay à lista vinda do IDB: add sobrescreve, LÁPIDE
 * REMOVE. Devolve ordenado por `addedAt` desc — a mesma ordem que o
 * `getFavorites` do IDB entregava antes, pra nenhuma tela ter que reordenar.
 *
 * @param fromIdb favoritos lidos do IDB (lista vazia se o IDB estiver morto)
 * @param entries entradas do overlay JÁ filtradas pelo scope corrente
 */
export function applyFavoritesOverlay(
  fromIdb: Favorite[],
  entries: FavoriteFallbackEntry[],
): Favorite[] {
  const byId = new Map<string, Favorite>()
  for (const f of fromIdb) byId.set(f.id, f)
  for (const e of entries) {
    if (e.deleted) byId.delete(e.id)
    else if (e.favorite) byId.set(e.id, e.favorite)
  }
  const out: Favorite[] = []
  byId.forEach((f) => out.push(f))
  return out.sort((a, b) => b.addedAt - a.addedAt)
}

// ── LIMPEZA EM MASSA: O OVERLAY SÓ ESVAZIA DEPOIS DO IDB ──
// "Limpar favoritos", "limpar dados do provider", excluir perfil: todos apagam
// um conjunto de registros do IDB. O erro fácil (e foi o que esta branch
// cometeu primeiro) é limpar o overlay ANTES, "pra lista esvaziar mesmo se o
// IDB falhar". Com a cota em zero o delete ABORTA NO COMMIT, os registros ficam
// no disco, e as LÁPIDES que a limpeza acabou de apagar eram justamente o que
// os escondia: o item que o usuário já tinha desfavoritado VOLTA. Uma limpeza
// que falha deixa o estado PIOR do que antes dela.
//
// Por isso são duas funções, uma para cada desfecho, e quem limpa chama a
// certa DEPOIS de saber o que o IDB fez:
//   - IDB aceitou   → `fallbackClearFavoritesWhere`: o disco já é a verdade;
//   - IDB recusou   → `fallbackTombstoneFavoritesWhere`: tudo o que a limpeza
//                     cobre vira LÁPIDE, inclusive cada registro lido do disco
//                     (a leitura funciona com a cota em zero; é a escrita que
//                     aborta).
// O `matches` recebe o `providerScope` da entrada e diz se a limpeza a cobre.

/** O IDB ACEITOU uma limpeza em massa: remove do overlay as entradas cobertas
 *  (adds e lápides; o disco já não tem o que elas corrigiam).
 *  @returns `true` se o bucket reflete a limpeza (inclusive quando não havia
 *  nada a remover). */
export function fallbackClearFavoritesWhere(matches: (providerScope: string) => boolean): boolean {
  const list = readAll()
  const next = list.filter((e) => !matches(e.providerScope))
  if (next.length === list.length) return true
  return writeAll(next)
}

/**
 * O IDB RECUSOU uma limpeza em massa: os registros continuam no disco. Toda
 * entrada coberta vira LÁPIDE, e cada registro em `onDisk` ganha a sua.
 *
 * Um add pendente também vira lápide (e não some): o id dele pode estar no
 * disco de antes, e a lápide é inofensiva quando não está.
 *
 * ⚠️ Custo: uma lápide por favorito do escopo, disputando o teto de
 * `FAVORITES_FALLBACK_MAX_ENTRIES` com os outros escopos. É o preço de não
 * ressuscitar; o produto trabalha com <100 por escopo.
 *
 * @param onDisk os registros que a limpeza tentou apagar do IDB (lidos do
 *   índice antes da escrita abortar). Lista vazia se nem a leitura foi possível.
 * @returns `true` se as lápides foram gravadas.
 */
export function fallbackTombstoneFavoritesWhere(
  matches: (providerScope: string) => boolean,
  onDisk: ReadonlyArray<{ id: string; providerScope: string }>,
): boolean {
  const now = Date.now()
  const kept: FavoriteFallbackEntry[] = []
  const tombstones = new Map<string, FavoriteFallbackEntry>()
  for (const e of readAll()) {
    if (matches(e.providerScope)) {
      tombstones.set(e.id, { id: e.id, providerScope: e.providerScope, deleted: true, updatedAt: now })
    } else {
      kept.push(e)
    }
  }
  for (const r of onDisk) {
    tombstones.set(r.id, { id: r.id, providerScope: r.providerScope, deleted: true, updatedAt: now })
  }
  tombstones.forEach((t) => kept.push(t))
  return writeAll(kept)
}

/** Zera o bucket inteiro. Só DEPOIS de o banco inteiro ter saído (hard reset do
 *  local-db): antes disso as lápides ainda escondem registros do disco. */
export function fallbackClearFavoritesAll(): void {
  const s = store()
  if (!s) return
  try {
    s.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
